import React from 'react';
import { MapPin, ChevronRight } from 'lucide-react';

const popularSuburbs = {
  adelaide: [
    { name: 'Mount Barker', slug: 'mount-barker' },
    { name: 'Two Wells', slug: 'two-wells' },
    { name: 'Munno Para', slug: 'munno-para' },
  ],
  melbourne: [
    { name: 'Tarneit', slug: 'tarneit' },
    { name: 'Craigieburn', slug: 'craigieburn' },
    { name: 'Clyde North', slug: 'clyde-north' },
  ],
  sydney: [
    { name: 'Marsden Park', slug: 'marsden-park' },
    { name: 'Oran Park', slug: 'oran-park' },
    { name: 'Box Hill', slug: 'box-hill-nsw' },
  ],
  brisbane: [
    { name: 'Springfield', slug: 'springfield' },
    { name: 'North Lakes', slug: 'north-lakes' },
    { name: 'Yarrabilba', slug: 'yarrabilba' },
  ],
  perth: [
    { name: 'Baldivis', slug: 'baldivis' },
    { name: 'Ellenbrook', slug: 'ellenbrook' },
    { name: 'Piara Waters', slug: 'piara-waters' },
  ],
};

// Total suburb counts per city (actual suburbs in each city)
const suburbCounts = {
  adelaide: 430,
  melbourne: 350,
  sydney: 650,
  brisbane: 400,
  perth: 350,
};

const cities = [
  { name: 'Adelaide', slug: 'adelaide', state: 'SA' },
  { name: 'Melbourne', slug: 'melbourne', state: 'VIC' },
  { name: 'Sydney', slug: 'sydney', state: 'NSW' },
  { name: 'Brisbane', slug: 'brisbane', state: 'QLD' },
  { name: 'Perth', slug: 'perth', state: 'WA' },
];

const LocationsSection = () => {
  return (
    <section className="py-12 sm:py-20 pb-8 sm:pb-12 bg-white overflow-hidden" id="locations">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
            <MapPin className="w-4 h-4" />
            Australia Wide
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-gray-900 mb-4">
            Building Support for <span className="gradient-text">Every Location</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Find local building information, council details, and popular estates for suburbs across Australia.
          </p>
        </div>

        {/* Desktop only: Full expanded grid with suburb links */}
        <div className="hidden lg:grid lg:grid-cols-5 gap-6">
          {cities.map((city) => (
            <div key={city.slug} className="bg-neutral-50 rounded-2xl p-6 hover:shadow-lg transition-shadow">
              <a 
                href={`/building-in-${city.slug}`}
                className="flex items-center justify-between mb-2 group"
              >
                <h3 className="font-semibold text-lg text-gray-900 group-hover:text-purple-600 transition-colors">
                  {city.name}
                </h3>
                <span className="text-xs font-medium text-gray-500 bg-white px-2 py-1 rounded">
                  {city.state}
                </span>
              </a>
              <p className="text-xs text-purple-600 font-medium mb-4">
                +{suburbCounts[city.slug]} suburbs
              </p>
              
              <ul className="space-y-2">
                {popularSuburbs[city.slug].map((suburb) => (
                  <li key={suburb.slug}>
                    <a 
                      href={`/building-in-${suburb.slug}`}
                      className="flex items-center text-sm text-gray-600 hover:text-purple-600 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 mr-1 text-gray-400" />
                      {suburb.name}
                    </a>
                  </li>
                ))}
              </ul>
              
              <a 
                href={`/building-in-${city.slug}`}
                className="inline-flex items-center text-sm font-medium text-purple-600 mt-4 hover:text-purple-700"
              >
                View all {city.name} suburbs
                <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
          ))}
        </div>

        {/* Tablet (iPad): Compact 3-column grid */}
        <div className="hidden sm:block lg:hidden">
          <div className="grid grid-cols-3 gap-3 mb-4">
            {cities.map((city) => (
              <a 
                key={city.slug}
                href={`/building-in-${city.slug}`}
                className="bg-neutral-50 rounded-xl p-4 hover:shadow-md transition-shadow group"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                    {city.name}
                  </h3>
                  <span className="text-xs font-medium text-gray-400">
                    {city.state}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {popularSuburbs[city.slug].map(s => s.name).join(', ')} <span className="text-purple-600 font-medium">+{suburbCounts[city.slug]} suburbs</span>
                </p>
              </a>
            ))}
          </div>
        </div>

        {/* Mobile: Compact 2-column grid with minimal info */}
        <div className="sm:hidden">
          <div className="grid grid-cols-2 gap-3 mb-4">
            {cities.map((city) => (
              <a 
                key={city.slug}
                href={`/building-in-${city.slug}`}
                className="bg-neutral-50 rounded-xl p-4 hover:shadow-md transition-shadow group"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                    {city.name}
                  </h3>
                  <span className="text-xs font-medium text-gray-400">
                    {city.state}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {popularSuburbs[city.slug].slice(0, 2).map(s => s.name).join(', ')} <span className="text-purple-600 font-medium">+{suburbCounts[city.slug]} suburbs</span>
                </p>
              </a>
            ))}
          </div>
        </div>

        {/* App Screenshots Showcase */}
        <div className="mt-16 sm:mt-20 pt-16 sm:pt-20 border-t border-gray-200">
          <h3 className="text-center font-serif text-2xl sm:text-3xl text-gray-900 mb-6 sm:mb-8">
            Support at <span className="gradient-text">Every Stage</span>
          </h3>
          <div className="flex justify-center items-start gap-3 sm:gap-6 lg:gap-8 px-2">
            {/* Screenshot 1 - Defect Scanner */}
            <div className="group cursor-pointer flex flex-col items-center">
              <div className="w-[80px] sm:w-[130px] lg:w-[170px] rounded-2xl overflow-hidden transition-transform duration-300 ease-out group-hover:-translate-y-3">
                <img 
                  src="/app-screenshot-defects.webp" 
                  alt="BuildPilot App Defect Scanner - Understand what a defect is and how serious it may be" 
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="text-xs sm:text-sm text-center font-semibold mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 max-w-[100px] sm:max-w-[140px] lg:max-w-[180px]">
                <span className="gradient-text">Understand what a defect is, how serious it may be, and what to do next in seconds.</span>
              </p>
            </div>
            
            {/* Screenshot 2 - Milestone Tracking */}
            <div className="group cursor-pointer flex flex-col items-center">
              <div className="w-[80px] sm:w-[130px] lg:w-[170px] rounded-2xl overflow-hidden transition-transform duration-300 ease-out group-hover:-translate-y-3">
                <img 
                  src="/app-screenshot-support.webp" 
                  alt="BuildPilot App Milestone Tracking - Stay on top of your build progress" 
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="text-xs sm:text-sm text-center font-semibold mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 max-w-[100px] sm:max-w-[140px] lg:max-w-[180px]">
                <span className="gradient-text">Stay on top of your build progress and know exactly what each stage means.</span>
              </p>
            </div>
            
            {/* Screenshot 3 - Design & Improvement Visualisation */}
            <div className="group cursor-pointer flex flex-col items-center">
              <div className="w-[80px] sm:w-[130px] lg:w-[170px] rounded-2xl overflow-hidden transition-transform duration-300 ease-out group-hover:-translate-y-3">
                <img 
                  src="/app-screenshot-design.webp" 
                  alt="BuildPilot App Design Visualisation - See how your ideas and upgrades could look" 
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="text-xs sm:text-sm text-center font-semibold mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 max-w-[100px] sm:max-w-[140px] lg:max-w-[180px]">
                <span className="gradient-text">See how your ideas and upgrades could look before you commit to them.</span>
              </p>
            </div>
            
            {/* Screenshot 4 - Style Matching */}
            <div className="group cursor-pointer flex flex-col items-center">
              <div className="w-[80px] sm:w-[130px] lg:w-[170px] rounded-2xl overflow-hidden transition-transform duration-300 ease-out group-hover:-translate-y-3">
                <img 
                  src="/app-screenshot-style.webp" 
                  alt="BuildPilot App Style Matching - Find furniture and styling to match the look you love" 
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="text-xs sm:text-sm text-center font-semibold mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 max-w-[100px] sm:max-w-[140px] lg:max-w-[180px]">
                <span className="gradient-text">Take a photo of a space you love and find furniture and styling to match the look.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
